import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntitlement } from '../hooks/useEntitlement';
import { createSubscribeHandoffUrl } from '../api/subscribeHandoff';
import { getCampaignPromo } from '../lib/campaignParams';

function todayDismissKey(): string {
  return `hbs:trial-banner-dismissed-${new Date().toISOString().slice(0, 10)}`;
}

/**
 * Top-of-page banner shown for the entire trial window — any time the user
 * is in `state === 'trial'` with a non-null `trialDaysLeft` and hasn't
 * dismissed today's banner. Dismissal lasts one calendar day — the next
 * day's banner has its own sessionStorage key so the user sees it again.
 *
 * When an `activePromoCode` is present the banner promotes the code with
 * its headline copy; otherwise it falls back to the plain "Subscribe to
 * keep your family" copy. The Subscribe button passes `promo: true` to the
 * handoff URL ONLY when a code is active, so the server auto-attaches the
 * code at Checkout only for promo-originated arrivals — other entry points
 * (Manage subscription, etc.) do not.
 */
export function TrialBanner() {
  const { state, trialDaysLeft, activePromoCode, snapshot } = useEntitlement();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(todayDismissKey()) === '1';
    } catch {
      return false;
    }
  });
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDismiss = useCallback(() => {
    try { sessionStorage.setItem(todayDismissKey(), '1'); } catch { /* ignore */ }
    setDismissed(true);
  }, []);

  // Mint a one-time handoff token (same flow as Settings → Manage
  // subscription) so the subscribe app can bootstrap a session and
  // doesn't reject the user as unauthorized. Navigate the current tab
  // (not a new one) so "Return to safe" from the subscribe app lands the
  // user back here instead of leaving a second safe tab open — matches
  // SettingsPage's Manage-subscription button.
  const handleSubscribe = useCallback(async () => {
    setError(null);
    setOpening(true);
    try {
      // A campaign code beats the global one: activePromoCode is
      // pickBestPromoCode(...) server-side, i.e. codes[0], so without this the
      // ad's advertised discount loses to whatever sorts first in the cache.
      // The server re-validates against Stripe, so a bad campaign code fails
      // closed rather than silently discounting.
      const url = await createSubscribeHandoffUrl({
        promoCode: getCampaignPromo() ?? activePromoCode?.code,
      });
      window.location.href = url;
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOpening(false);
    }
  }, [activePromoCode]);

  if (state !== 'trial') return null;

  // Paid but not yet linked to a family: show a "finish setup" prompt instead of
  // the trial/promo banner. Renders regardless of the per-day dismissal and the
  // day-count guard. Auto-clears once a family is created (snapshot → active-subscriber).
  if (snapshot?.hasUnusedActiveSub === true) {
    return (
      <div className="bg-yellow-100 border-b border-yellow-400 px-4 py-2 flex items-center justify-between gap-4">
        <span className="text-sm text-yellow-950">
          You&apos;re subscribed <span aria-hidden="true">🎉</span> Create your family safe to start sharing.
        </span>
        <button
          type="button"
            id="trial-create-family-button"
            onClick={() => navigate('/orgs/new')}
          className="shrink-0 text-yellow-950 underline font-medium hover:text-yellow-700"
        >
          Create family
        </button>
      </div>
    );
  }

  if (trialDaysLeft === null) return null;
  if (dismissed) return null;

  const subscribeButton = (
    <button
      type="button"
      data-testid="subscribe-cta"
          id="trial-subscribe-button"
          onClick={handleSubscribe}
      disabled={opening}
      className="text-yellow-950 underline font-medium hover:text-yellow-700 disabled:opacity-60"
    >
      {opening ? 'Opening…' : 'Subscribe'}
    </button>
  );

  return (
    <div className="bg-yellow-100 border-b border-yellow-400 px-4 py-2 flex items-center justify-between gap-4">
      <span className="text-sm text-yellow-950">
        Your trial ends in {trialDaysLeft} {trialDaysLeft === 1 ? 'day' : 'days'}.{' '}
        {activePromoCode ? (
          <>
            Use <span className="font-mono font-semibold">{activePromoCode.code}</span>
            {' '}for {activePromoCode.headline}.{' '}
            {subscribeButton}
          </>
        ) : (
          <>
            {subscribeButton}{' '}
            to keep your family.
          </>
        )}
        {error && <span className="ml-2 text-red-700">{error}</span>}
      </span>
      <button
        type="button"
        id="trial-banner-dismiss-button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="text-yellow-950 hover:text-yellow-700 text-sm"
      >
        ×
      </button>
    </div>
  );
}
